import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, Plus, Minus } from 'lucide-react';
import AnimateOnScroll from './AnimateOnScroll';

const Services = () => {
  const { t } = useLanguage();
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      num: '01',
      title: { fr: 'Construction Neuve et Rénovation', en: 'New Construction and Renovation' },
      desc: { fr: 'Construction de maisons individuelles, immeubles résidentiels et bâtiments commerciaux, ainsi que rénovation complète de bâtiments existants. Nous intervenons aussi bien sur des projets neufs que sur la transformation et la modernisation de votre patrimoine.', en: 'Construction of individual houses, residential buildings and commercial properties, as well as complete renovation of existing buildings. We work on both new projects and the transformation and modernization of your property.' },
    },
    {
      num: '02',
      title: { fr: 'Rénovation Énergétique', en: 'Energy Renovation' },
      desc: { fr: 'Transformation de bâtiments existants pour améliorer leur performance énergétique et réduire leur empreinte carbone.', en: 'Transformation of existing buildings to improve energy performance and reduce carbon footprint.' },
    },
    {
      num: '03',
      title: { fr: 'Surélévation', en: 'Building Extension' },
      desc: { fr: 'Ajout d\'étages supplémentaires à des structures existantes, maximisant l\'espace sans compromettre l\'intégrité.', en: 'Adding additional floors to existing structures, maximizing space without compromising integrity.' },
    },
    {
      num: '04',
      title: { fr: 'Construction Bois', en: 'Timber Construction' },
      desc: { fr: 'Spécialistes de la construction en bois, matériau naturel et renouvelable pour des bâtiments écologiques.', en: 'Specialists in timber construction, a natural and renewable material for ecological buildings.' },
    },
    {
      num: '05',
      title: { fr: 'Construction Béton', en: 'Concrete Construction' },
      desc: { fr: 'Nous réalisons également des constructions traditionnelles en béton selon les souhaits du client, tout en intégrant les meilleures pratiques durables. Notre objectif reste de minimiser l\'impact environnemental, même dans la construction conventionnelle.', en: 'We also carry out traditional concrete constructions according to client wishes, while integrating the best sustainable practices. Our goal remains to minimize environmental impact, even in conventional construction.' },
    },
    {
      num: '06',
      title: { fr: 'Livraison Clé en Main', en: 'Turnkey Delivery' },
      desc: { fr: 'Gestion complète du projet de la conception à la livraison, avec prix et délais garantis.', en: 'Complete project management from design to delivery, with guaranteed price and timeline.' },
    },
  ];

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="bg-neutral-50 py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="grid lg:grid-cols-2 gap-16 mb-20">
          <AnimateOnScroll animation="fade-up">
            <p className="text-neutral-500 text-sm tracking-[0.3em] uppercase mb-4">
              {t({ fr: 'Nos Services', en: 'Our Services' })}
            </p>
            <h2 className="text-5xl md:text-7xl font-light text-neutral-900 leading-[1.1]">
              {t({ fr: 'Ce que', en: 'What' })}
              <br />
              <span className="text-neutral-400">{t({ fr: 'nous faisons', en: 'we do' })}</span>
            </h2>
          </AnimateOnScroll>
          
          <AnimateOnScroll animation="fade-up" delay={1}>
            <div className="lg:pt-8">
              <p className="text-neutral-500 text-lg leading-relaxed mb-8">
                {t({ 
                  fr: 'De la conception à la réalisation, nous vous accompagnons à chaque étape de votre projet immobilier.',
                  en: 'From design to completion, we support you at every stage of your real estate project.'
                })}
              </p>
              <button
                onClick={scrollToContact}
                className="group inline-flex items-center space-x-3 text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                <span>{t({ fr: 'Démarrer votre projet', en: 'Start your project' })}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </AnimateOnScroll>
        </div>

        {/* Services Accordion */}
        <div className="border-t border-neutral-200">
          {services.map((service, index) => (
            <AnimateOnScroll key={service.num} animation="fade-up" delay={index % 3}>
              <div className="border-b border-neutral-200">
                <button
                  onClick={() => setActiveService(activeService === index ? -1 : index)}
                  className="w-full py-8 flex items-center justify-between text-left group"
                >
                  <div className="flex items-center space-x-8">
                    <span className="text-neutral-400 text-sm font-mono">{service.num}/</span>
                    <h3 className={`text-2xl md:text-3xl font-light transition-colors ${
                      activeService === index ? 'text-neutral-500' : 'text-neutral-800 group-hover:text-neutral-600'
                    }`}>
                      {t(service.title)}
                    </h3>
                  </div>
                  <div className="text-neutral-400">
                    {activeService === index ? (
                      <Minus className="w-6 h-6" />
                    ) : (
                      <Plus className="w-6 h-6" />
                    )}
                  </div>
                </button>
                
                {activeService === index && (
                  <div className="pb-8 pl-20 pr-12">
                    <p className="text-neutral-500 text-lg max-w-2xl leading-relaxed">
                      {t(service.desc)}
                    </p>
                  </div>
                )}
              </div>
            </AnimateOnScroll>
          ))}
        </div>

        {/* Video Section */}
        <AnimateOnScroll animation="scale" className="mt-20">
          <div className="relative aspect-video overflow-hidden">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            >
              <source src="https://customer-assets.emergentagent.com/job_pt-text-input-1/artifacts/bz7r61z2_Developpement_Immo.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-black/40"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-white/80 text-sm tracking-[0.3em] uppercase mb-4">
                  {t({ fr: 'Développement Immobilier', en: 'Real Estate Development' })}
                </p>
                <h3 className="text-4xl md:text-6xl font-light text-white">
                  {t({ fr: 'Offres Disponibles', en: 'Available Offers' })}
                </h3>
              </div>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default Services;
